/**
 * StorageManagementPage — admin-only S3 storage dashboard.
 *
 * Tabs:
 *   1. Overview      — live totals + tier breakdown + growth chart
 *   2. Lifecycle     — read / edit / apply-defaults for S3 lifecycle rules
 *   3. Analytics     — storage usage by customer / order / file type
 *   4. Cost          — Cost Explorer current spend + savings simulator
 *
 * Notes:
 *  - Every AWS-dependent panel gracefully renders an "IAM policy required"
 *    banner when the endpoint responds with an `aws_error` field.
 *  - CloudWatch data is cached 15 min server-side; Cost Explorer 24h.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/alert';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend, CartesianGrid,
} from 'recharts';
import {
    HardDrive, RefreshCw, TrendingUp, Users, FileText, DollarSign,
    AlertTriangle, Layers, PiggyBank, Snowflake, Database, ShieldAlert,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ---------- utilities ----------------------------------------------------
const fmtBytes = (b) => {
    if (b == null) return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = Number(b) || 0;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
};
const fmtUSD = (v) => v == null ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (v) => v == null ? '—' : Number(v).toLocaleString('en-US');

const TIER_COLORS = ['#6366f1', '#818cf8', '#0ea5e9', '#22d3ee', '#8b5cf6', '#a78bfa', '#94a3b8'];

// ---------- shared: AWS error banner -------------------------------------
const AwsErrorBanner = ({ error }) => {
    if (!error) return null;
    return (
        <Alert variant="destructive" className="mb-4" data-testid="aws-error-banner">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>AWS permission required</AlertTitle>
            <AlertDescription className="mt-1">
                <div className="text-xs mt-1">
                    <div><strong>Error:</strong> {error.code}</div>
                    <div><strong>Missing action:</strong> <code className="bg-white/60 px-1 rounded">{error.action_required}</code></div>
                    <div className="mt-2">
                        Attach the read-only Storage Dashboard IAM policy to <code>vinylflow-app</code> to enable this panel.
                        See the &quot;IAM policy JSON&quot; section at the bottom of the Overview tab.
                    </div>
                </div>
            </AlertDescription>
        </Alert>
    );
};

// ---------- Overview tab -------------------------------------------------
export const OverviewTab = () => {
    const [overview, setOverview] = useState(null);
    const [growth, setGrowth] = useState(null);
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [snapshotting, setSnapshotting] = useState(false);

    const load = useCallback(async (refresh = false) => {
        const setter = refresh ? setRefreshing : setLoading;
        setter(true);
        try {
            const [o, g, h] = await Promise.all([
                axios.get(`${API}/api/admin/storage/overview${refresh ? '?refresh=true' : ''}`),
                axios.get(`${API}/api/admin/storage/growth${refresh ? '?refresh=true' : ''}`),
                axios.get(`${API}/api/admin/storage/history?days=365`),
            ]);
            setOverview(o.data);
            setGrowth(g.data);
            setHistory(h.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load storage overview');
        } finally {
            setter(false);
        }
    }, []);

    useEffect(() => { load(false); }, [load]);

    const takeSnapshot = async () => {
        setSnapshotting(true);
        try {
            const r = await axios.post(`${API}/api/admin/storage/snapshot`);
            if (r.data.status === 'ok') {
                toast.success(`Snapshot captured for ${r.data.date}`);
                load(false);
            } else {
                toast.warning(r.data.reason || 'Snapshot skipped');
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Snapshot failed');
        } finally {
            setSnapshotting(false);
        }
    };

    if (loading && !overview) return <div className="text-sm text-slate-500 p-6">Loading storage overview…</div>;

    const tiers = (overview?.tiers || []).filter(t => (t.bytes || 0) > 0);
    // Prefer the local snapshot series when we have >=7 usable days —
    // it goes back further than CloudWatch's 15-month window and survives
    // IAM outages.
    const snapshotSeries = (history?.snapshots || []).map(s => ({
        date: s.date,
        gb: +(s.total_gb ?? (s.total_bytes / (1024 ** 3))).toFixed(3),
    }));
    const cloudwatchSeries = (growth?.series || []).map(p => ({
        date: p.date.slice(0, 10),
        gb: +(p.bytes / (1024 ** 3)).toFixed(3),
    }));
    const useSnapshotSeries = snapshotSeries.length >= 7;
    const growthSeries = useSnapshotSeries ? snapshotSeries : cloudwatchSeries;

    return (
        <div className="space-y-6" data-testid="storage-overview">
            <AwsErrorBanner error={overview?.aws_error} />

            {/* Top stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={<HardDrive className="h-4 w-4 text-indigo-600" />} label="Total storage" value={fmtBytes(overview?.total_bytes)} />
                <StatCard icon={<Database className="h-4 w-4 text-blue-600" />} label="Objects" value={fmtInt(overview?.object_count)} />
                <StatCard icon={<Layers className="h-4 w-4 text-violet-600" />} label="Avg. object size" value={fmtBytes(overview?.average_object_size_bytes)} />
                <StatCard icon={<DollarSign className="h-4 w-4 text-emerald-600" />} label="Est. monthly cost" value={fmtUSD(overview?.monthly_cost_estimate_usd)} />
            </div>

            {/* Reconciliation breakdown — explains any delta between the
                snapshot totals (source of truth) and CloudWatch reported. */}
            {overview?.reconciliation && (
                <Card data-testid="reconciliation-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Layers className="h-4 w-4 text-indigo-600" /> Storage reconciliation
                        </CardTitle>
                        <CardDescription>
                            Every tab on this page uses the same snapshot as source of truth.
                            {overview.cloudwatch_reported && (
                                <span className="ml-1">
                                    CloudWatch reports {fmtBytes(overview.cloudwatch_reported.total_bytes)} / {fmtInt(overview.cloudwatch_reported.object_count)} objects (may include multipart uploads and versioned objects).
                                </span>
                            )}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <ReconCell
                                label="DB + S3 (tracked)"
                                bytes={overview.reconciliation.db_plus_s3.bytes}
                                count={overview.reconciliation.db_plus_s3.count}
                                tint="emerald"
                                testid="recon-db-s3"
                            />
                            <ReconCell
                                label="S3 only (orphaned)"
                                bytes={overview.reconciliation.s3_only.bytes}
                                count={overview.reconciliation.s3_only.count}
                                tint="amber"
                                testid="recon-s3-only"
                            />
                            <ReconCell
                                label="DB only (missing on S3)"
                                bytes={overview.reconciliation.db_only.bytes}
                                count={overview.reconciliation.db_only.count}
                                tint="rose"
                                testid="recon-db-only"
                            />
                            <ReconCell
                                label="Unmatched (no order)"
                                bytes={overview.reconciliation.unmatched_no_order.bytes}
                                count={overview.reconciliation.unmatched_no_order.count}
                                tint="slate"
                                testid="recon-unmatched"
                            />
                        </div>
                        {(overview.reconciliation.excluded_folder_placeholders?.count || 0) > 0 && (
                            <p className="text-xs text-slate-500 mt-3">
                                {overview.reconciliation.excluded_folder_placeholders.count} zero-byte folder placeholder{overview.reconciliation.excluded_folder_placeholders.count === 1 ? '' : 's'} excluded from totals.
                            </p>
                        )}
                        {overview.cloudwatch_reported && (
                            <p className="text-xs text-slate-500 mt-2">
                                Delta vs CloudWatch: {fmtBytes(Math.max(0, (overview.cloudwatch_reported.total_bytes || 0) - (overview.total_bytes || 0)))} likely from multipart uploads / object versions not in the live listing.
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}

            <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">
                    Bucket <code className="bg-slate-100 px-1 rounded">{overview?.bucket}</code> · region {overview?.region}
                    {overview?.cached && ' · cached'}
                    {history?.count > 0 && (
                        <span className="ml-2 text-emerald-700">
                            · {history.count} daily snapshots stored
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={takeSnapshot} disabled={snapshotting} data-testid="snapshot-now-btn">
                        <Database className={`h-3 w-3 mr-1 ${snapshotting ? 'animate-pulse' : ''}`} /> Snapshot now
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => load(true)} disabled={refreshing} data-testid="refresh-overview">
                        <RefreshCw className={`h-3 w-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh from AWS
                    </Button>
                </div>
            </div>

            {/* Tier breakdown */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2"><Layers className="h-4 w-4" /> Storage by tier</CardTitle>
                    <CardDescription>Per storage class — bytes, files, estimated monthly cost, and share of total.</CardDescription>
                </CardHeader>
                <CardContent>
                    {tiers.length === 0 ? (
                        <p className="text-sm text-slate-500 py-6 text-center">No CloudWatch metrics available yet. Metrics appear ~24h after the first upload once IAM permissions are attached.</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="md:col-span-1 h-64">
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie data={tiers} dataKey="bytes" nameKey="label" outerRadius={90} label={(e) => `${e.percent}%`}>
                                            {tiers.map((_, i) => <Cell key={i} fill={TIER_COLORS[i % TIER_COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip formatter={(v) => fmtBytes(v)} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="md:col-span-2 overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left border-b text-xs uppercase text-slate-500">
                                            <th className="py-1.5 pr-3">Class</th>
                                            <th className="py-1.5 pr-3 text-right">Size</th>
                                            <th className="py-1.5 pr-3 text-right">%</th>
                                            <th className="py-1.5 pr-3 text-right">Price/GB</th>
                                            <th className="py-1.5 text-right">Est. monthly</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(overview?.tiers || []).map((t, i) => (
                                            <tr key={t.storage_class} className="border-b hover:bg-slate-50">
                                                <td className="py-1.5 pr-3 flex items-center gap-2">
                                                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TIER_COLORS[i % TIER_COLORS.length] }} />
                                                    {t.label}
                                                </td>
                                                <td className="py-1.5 pr-3 text-right tabular-nums">{fmtBytes(t.bytes)}</td>
                                                <td className="py-1.5 pr-3 text-right tabular-nums">{t.percent}%</td>
                                                <td className="py-1.5 pr-3 text-right tabular-nums text-slate-500">${t.pricing_usd_per_gb.toFixed(5)}</td>
                                                <td className="py-1.5 text-right tabular-nums font-medium">{fmtUSD(t.monthly_cost_usd)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Growth */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Bucket growth</CardTitle>
                    <CardDescription>
                        {useSnapshotSeries
                            ? `Long-range series from ${snapshotSeries.length} daily snapshots (persisted by the nightly cron).`
                            : 'Standard tier only — from CloudWatch daily datapoints. A local snapshot cron runs nightly to build a longer history.'}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                        <MiniGrowth label="30 days" bytes={growth?.['30d']} />
                        <MiniGrowth label="90 days" bytes={growth?.['90d']} />
                        <MiniGrowth label="365 days" bytes={growth?.['365d']} />
                    </div>
                    {growthSeries.length > 0 ? (
                        <div className="h-64">
                            <ResponsiveContainer>
                                <LineChart data={growthSeries}>
                                    <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip formatter={(v) => `${v} GB`} />
                                    <Line type="monotone" dataKey="gb" stroke="#6366f1" strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-500 py-6 text-center">No growth data yet.</p>
                    )}
                </CardContent>
            </Card>

            {/* IAM helper */}
            <IamPolicyHelper />
        </div>
    );
};

const StatCard = ({ icon, label, value }) => (
    <Card>
        <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">{icon}{label}</div>
            <div className="mt-1 text-xl font-semibold tabular-nums" data-testid={`stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>{value}</div>
        </CardContent>
    </Card>
);

const MiniGrowth = ({ label, bytes }) => {
    const delta = bytes || 0;
    const isPos = delta >= 0;
    return (
        <div className="p-3 rounded-lg border bg-white">
            <div className="text-[10px] uppercase text-slate-500">{label}</div>
            <div className={`text-lg font-semibold ${isPos ? 'text-emerald-700' : 'text-red-700'}`}>
                {isPos ? '+' : ''}{fmtBytes(delta)}
            </div>
        </div>
    );
};

const ReconCell = ({ label, bytes, count, tint = 'slate', testid }) => {
    const tints = {
        emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
        amber: 'bg-amber-50 border-amber-200 text-amber-800',
        rose: 'bg-rose-50 border-rose-200 text-rose-800',
        slate: 'bg-slate-50 border-slate-200 text-slate-700',
    };
    return (
        <div className={`p-3 rounded-lg border ${tints[tint]}`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
            <div className="mt-1 text-lg font-semibold tabular-nums">{fmtBytes(bytes)}</div>
            <div className="text-xs opacity-70 tabular-nums">{fmtInt(count)} file{count === 1 ? '' : 's'}</div>
        </div>
    );
};

// ---------- Lifecycle tab ------------------------------------------------
export const LifecycleTab = () => {
    const [lifecycle, setLifecycle] = useState(null);
    const [defaults, setDefaults] = useState([]);
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [lc, def] = await Promise.all([
                axios.get(`${API}/api/admin/storage/lifecycle`),
                axios.get(`${API}/api/admin/storage/lifecycle/defaults`),
            ]);
            setLifecycle(lc.data);
            setRules(lc.data.rules || []);
            setDefaults(def.data.defaults || []);
        } catch (e) {
            toast.error('Failed to load lifecycle configuration');
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const applyDefaults = async () => {
        setSaving(true);
        try {
            const res = await axios.post(`${API}/api/admin/storage/lifecycle/apply-defaults`);
            toast.success('Default lifecycle policy applied');
            setRules(res.data.rules || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to apply defaults');
        } finally {
            setSaving(false);
        }
    };

    const saveRules = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/api/admin/storage/lifecycle`, {
                rules: rules.map(r => ({
                    ID: r.ID,
                    Status: r.Status,
                    Prefix: r.Prefix || '',
                    Transitions: r.Transitions || [],
                    Expiration: r.Expiration || null,
                })),
            });
            toast.success('Lifecycle configuration saved');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save lifecycle rules');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-6 text-sm text-slate-500">Loading lifecycle…</div>;

    return (
        <div className="space-y-4" data-testid="lifecycle-tab">
            <AwsErrorBanner error={lifecycle?.aws_error} />

            {rules.length === 0 && (
                <Alert>
                    <AlertTitle>No lifecycle policy configured</AlertTitle>
                    <AlertDescription>
                        Apply the recommended default (0-90 Standard → 90 Standard-IA → 180 Glacier IR → 365 Deep Archive) to start saving.
                    </AlertDescription>
                </Alert>
            )}

            <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={applyDefaults} disabled={saving || !!lifecycle?.aws_error} data-testid="apply-defaults-btn">
                    <Snowflake className="h-3 w-3 mr-1" /> Apply recommended defaults
                </Button>
                <Button size="sm" variant="outline" onClick={saveRules} disabled={saving || rules.length === 0 || !!lifecycle?.aws_error} data-testid="save-lifecycle-btn">
                    Save current rules
                </Button>
            </div>

            {/* Recommended defaults preview */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm">Recommended defaults</CardTitle>
                </CardHeader>
                <CardContent>
                    {defaults.map(r => (
                        <div key={r.ID} className="text-xs space-y-1">
                            <div className="font-medium text-slate-700">{r.ID}</div>
                            {(r.Transitions || []).map((t, i) => (
                                <div key={i} className="ml-3 text-slate-600">→ After {t.Days} days: move to <code className="bg-slate-100 px-1 rounded">{t.StorageClass}</code></div>
                            ))}
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Current rules */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm">Current rules ({rules.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    {rules.length === 0 && <p className="text-sm text-slate-500">No rules configured on the bucket.</p>}
                    {rules.map((rule, idx) => (
                        <RuleEditor
                            key={rule.ID || idx}
                            rule={rule}
                            onChange={(next) => setRules(rs => rs.map((r, i) => i === idx ? next : r))}
                            onRemove={() => setRules(rs => rs.filter((_, i) => i !== idx))}
                        />
                    ))}
                    <Button size="sm" variant="ghost" onClick={() => setRules(rs => [...rs, {
                        ID: `rule-${Date.now()}`, Status: 'Enabled', Prefix: '', Transitions: [], Expiration: null,
                    }])} data-testid="add-rule-btn">
                        + Add rule
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
};

const RuleEditor = ({ rule, onChange, onRemove }) => {
    const setTransition = (i, patch) => onChange({
        ...rule,
        Transitions: rule.Transitions.map((t, ii) => ii === i ? { ...t, ...patch } : t),
    });
    return (
        <div className="p-3 border rounded-lg bg-slate-50 space-y-2 text-sm">
            <div className="flex items-center gap-2">
                <Input value={rule.ID} onChange={e => onChange({ ...rule, ID: e.target.value })} className="max-w-xs" />
                <select
                    value={rule.Status}
                    onChange={e => onChange({ ...rule, Status: e.target.value })}
                    className="border rounded px-2 py-1 text-xs bg-white"
                >
                    <option value="Enabled">Enabled</option>
                    <option value="Disabled">Disabled</option>
                </select>
                <span className="text-xs text-slate-500">Prefix:</span>
                <Input value={rule.Prefix || ''} onChange={e => onChange({ ...rule, Prefix: e.target.value })} className="max-w-xs" placeholder="(empty = whole bucket)" />
                <Button size="sm" variant="ghost" onClick={onRemove} className="text-red-600 ml-auto">Remove</Button>
            </div>
            <div className="space-y-1.5">
                {(rule.Transitions || []).map((t, i) => (
                    <div key={i} className="flex items-center gap-2 ml-2 text-xs">
                        <span>After</span>
                        <Input type="number" value={t.Days || ''} onChange={e => setTransition(i, { Days: parseInt(e.target.value) || 0 })} className="w-20" />
                        <span>days → move to</span>
                        <select value={t.StorageClass || 'STANDARD_IA'} onChange={e => setTransition(i, { StorageClass: e.target.value })} className="border rounded px-2 py-1 text-xs bg-white">
                            {['STANDARD_IA', 'INTELLIGENT_TIERING', 'ONEZONE_IA', 'GLACIER_IR', 'GLACIER', 'DEEP_ARCHIVE'].map(c => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() =>
                            onChange({ ...rule, Transitions: rule.Transitions.filter((_, ii) => ii !== i) })
                        }>×</Button>
                    </div>
                ))}
                <Button size="sm" variant="ghost" onClick={() =>
                    onChange({ ...rule, Transitions: [...(rule.Transitions || []), { Days: 90, StorageClass: 'STANDARD_IA' }] })
                }>+ Add transition</Button>
            </div>
        </div>
    );
};

// ---------- Analytics tab ------------------------------------------------
export const AnalyticsTab = () => {
    const [byCustomer, setByCustomer] = useState([]);
    const [byOrder, setByOrder] = useState([]);
    const [byFileType, setByFileType] = useState([]);
    const [largest, setLargest] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [a, b, c, d] = await Promise.all([
                    axios.get(`${API}/api/admin/storage/analytics/by-customer`),
                    axios.get(`${API}/api/admin/storage/analytics/by-order`),
                    axios.get(`${API}/api/admin/storage/analytics/by-file-type`),
                    axios.get(`${API}/api/admin/storage/largest-files?limit=25`),
                ]);
                setByCustomer(a.data.customers || []);
                setByOrder(b.data.orders || []);
                setByFileType(c.data.file_types || []);
                setLargest(d.data.files || []);
            } catch (e) {
                toast.error('Failed to load analytics');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <div className="p-6 text-sm text-slate-500">Loading analytics…</div>;

    return (
        <div className="space-y-4" data-testid="analytics-tab">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* By customer */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4" /> By customer (top 10)</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-56">
                            <ResponsiveContainer>
                                <BarChart data={byCustomer.slice(0, 10)} layout="vertical" margin={{ left: 40 }}>
                                    <XAxis type="number" tick={{ fontSize: 10 }} />
                                    <YAxis type="category" dataKey="customer_name" tick={{ fontSize: 10 }} width={120} />
                                    <Tooltip formatter={(v) => fmtBytes(v)} />
                                    <Bar dataKey="total_bytes" fill="#6366f1" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* By file type */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm flex items-center gap-2"><FileText className="h-4 w-4" /> By file type</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-56">
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie data={byFileType} dataKey="total_bytes" nameKey="file_type" outerRadius={80} label={(e) => `${e.file_type}: ${e.percent}%`}>
                                        {byFileType.map((_, i) => <Cell key={i} fill={TIER_COLORS[i % TIER_COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip formatter={(v) => fmtBytes(v)} />
                                    <Legend wrapperStyle={{ fontSize: '10px' }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Orders table */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm">Top orders by storage</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-white">
                                <tr className="border-b text-left text-slate-500 uppercase">
                                    <th className="py-1.5 pr-3">Order #</th>
                                    <th className="py-1.5 pr-3">Title</th>
                                    <th className="py-1.5 pr-3">Status</th>
                                    <th className="py-1.5 pr-3 text-right">Files</th>
                                    <th className="py-1.5 text-right">Size</th>
                                </tr>
                            </thead>
                            <tbody>
                                {byOrder.slice(0, 30).map(o => (
                                    <tr key={o.order_id} className="border-b hover:bg-slate-50">
                                        <td className="py-1.5 pr-3 font-mono">{o.order_number || '—'}</td>
                                        <td className="py-1.5 pr-3">{o.order_title || '—'}</td>
                                        <td className="py-1.5 pr-3"><Badge variant="outline">{o.status || '—'}</Badge></td>
                                        <td className="py-1.5 pr-3 text-right tabular-nums">{o.file_count}</td>
                                        <td className="py-1.5 text-right tabular-nums">{fmtBytes(o.total_bytes)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            {/* Largest files */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm">Largest files</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-white">
                                <tr className="border-b text-left text-slate-500 uppercase">
                                    <th className="py-1.5 pr-3">Filename</th>
                                    <th className="py-1.5 pr-3">Order</th>
                                    <th className="py-1.5 pr-3">Customer</th>
                                    <th className="py-1.5 pr-3">Type</th>
                                    <th className="py-1.5 text-right">Size</th>
                                </tr>
                            </thead>
                            <tbody>
                                {largest.map(f => (
                                    <tr key={f.id} className="border-b hover:bg-slate-50">
                                        <td className="py-1.5 pr-3 truncate max-w-xs" title={f.filename}>{f.filename}</td>
                                        <td className="py-1.5 pr-3 font-mono">{f.order_number || '—'}</td>
                                        <td className="py-1.5 pr-3">{f.customer_name || '—'}</td>
                                        <td className="py-1.5 pr-3">{f.file_type || '—'}</td>
                                        <td className="py-1.5 text-right tabular-nums">{fmtBytes(f.file_size)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

// ---------- Cost tab -----------------------------------------------------
export const CostTab = () => {
    const [current, setCurrent] = useState(null);
    const [savings, setSavings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(90);
    const [target, setTarget] = useState('GLACIER_IR');

    const loadCurrent = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/api/admin/storage/cost/current`);
            setCurrent(r.data);
        } catch (e) {
            // Backend now returns 200 with aws_error even on AccessDenied,
            // so a real exception means something more serious (network,
            // 5xx). Surface it verbatim.
            toast.error(e?.response?.data?.detail || 'Cost Explorer unavailable');
            setCurrent({ aws_error: {
                code: 'Error',
                message: e?.message || 'unknown',
                action_required: 'network / server',
            } });
        }
    }, []);

    const loadSavings = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/api/admin/storage/cost/savings-estimate?move_after_days=${days}&target_class=${target}`);
            setSavings(r.data);
        } catch (e) {
            toast.error('Failed to compute savings estimate');
        }
    }, [days, target]);

    useEffect(() => {
        (async () => {
            await Promise.all([loadCurrent(), loadSavings()]);
            setLoading(false);
        })();
    }, [loadCurrent, loadSavings]);

    if (loading) return <div className="p-6 text-sm text-slate-500">Loading cost data…</div>;

    return (
        <div className="space-y-4" data-testid="cost-tab">
            <AwsErrorBanner error={current?.aws_error} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <StatCard icon={<DollarSign className="h-4 w-4 text-emerald-600" />} label="This month (S3)" value={current?.current_month_usd != null ? fmtUSD(current.current_month_usd) : '—'} />
                <StatCard icon={<DollarSign className="h-4 w-4 text-blue-600" />} label="Previous month (S3)" value={current?.previous_month_usd != null ? fmtUSD(current.previous_month_usd) : '—'} />
                <StatCard icon={<PiggyBank className="h-4 w-4 text-violet-600" />} label="Monthly savings (est.)" value={savings ? fmtUSD(savings.monthly_savings_usd) : '—'} />
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2"><PiggyBank className="h-4 w-4" /> Savings simulator</CardTitle>
                    <CardDescription>
                        Estimate the monthly savings if every file older than N days were moved to a colder tier.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-wrap items-end gap-3">
                        <div>
                            <Label className="text-xs">Move after (days)</Label>
                            <Input type="number" min={30} max={730} value={days} onChange={e => setDays(parseInt(e.target.value) || 90)} className="w-28" />
                        </div>
                        <div>
                            <Label className="text-xs">Target class</Label>
                            <select value={target} onChange={e => setTarget(e.target.value)} className="border rounded px-2 py-1 text-sm bg-white h-9">
                                {['STANDARD_IA', 'INTELLIGENT_TIERING', 'ONEZONE_IA', 'GLACIER_IR', 'GLACIER', 'DEEP_ARCHIVE'].map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>
                        <Button size="sm" onClick={loadSavings}>Recalculate</Button>
                    </div>
                    {savings && (
                        <div className="text-sm space-y-1">
                            <div>Eligible files: <strong>{fmtInt(savings.eligible_files)}</strong> ({savings.eligible_gb} GB)</div>
                            <div>Current monthly cost: <strong>{fmtUSD(savings.current_monthly_usd)}</strong></div>
                            <div>Target monthly cost: <strong>{fmtUSD(savings.target_monthly_usd)}</strong></div>
                            <div className="text-emerald-700 font-semibold">
                                Monthly savings: {fmtUSD(savings.monthly_savings_usd)} · Annual: {fmtUSD(savings.annual_savings_usd)}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

// ---------- Files tab (global S3 listing) -------------------------------
export const FilesTab = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalBytes, setTotalBytes] = useState(0);
    const [facets, setFacets] = useState({ file_types: [], storage_classes: [], sources: [] });
    const [awsError, setAwsError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(100);
    const [filters, setFilters] = useState({
        customer_id: '', order_id: '', file_type: '', source: '',
        storage_class: '', min_size: '', max_size: '', search: '',
    });
    const [repairing, setRepairing] = useState(null); // s3_key being repaired

    // Debounced load — fetch when filters or page change.
    useEffect(() => {
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const params = { page, page_size: pageSize };
                Object.entries(filters).forEach(([k, v]) => {
                    if (v !== '' && v != null) params[k] = v;
                });
                const res = await axios.get(`${API}/api/admin/storage/files`, { params });
                setRows(res.data.files || []);
                setTotal(res.data.total || 0);
                setTotalBytes(res.data.total_bytes || 0);
                setFacets(res.data.facets || { file_types: [], storage_classes: [], sources: [] });
                setAwsError(res.data.aws_error || null);
            } catch (e) {
                toast.error(e?.response?.data?.detail || 'Failed to load files');
            } finally {
                setLoading(false);
            }
        }, 350);
        return () => clearTimeout(t);
    }, [page, pageSize, filters]);

    const download = async (row) => {
        try {
            const res = await axios.post(`${API}/api/admin/storage/files/presigned-download`, {
                s3_key: row.s3_key,
            });
            if (res.data?.download_url) window.open(res.data.download_url, '_blank');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Download failed');
        }
    };

    const copyPath = (row) => {
        navigator.clipboard.writeText(row.s3_path || '');
        toast.success('S3 path copied');
    };

    const repair = async (row) => {
        setRepairing(row.s3_key);
        try {
            const res = await axios.post(`${API}/api/admin/storage/files/repair`, {
                s3_key: row.s3_key,
            });
            if (res.data?.status === 'created') {
                toast.success('DB record created');
                setRows(rs => rs.map(r => r.s3_key === row.s3_key ? { ...r, source: 'db+s3', id: res.data.file_id } : r));
            } else if (res.data?.status === 'already_exists') {
                toast.info('DB record already exists');
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Repair failed');
        } finally {
            setRepairing(null);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const setFilter = (k, v) => { setPage(1); setFilters(f => ({ ...f, [k]: v })); };

    const SourceBadge = ({ src }) => {
        const styles = {
            'db+s3': 'bg-emerald-100 text-emerald-800 border-emerald-300',
            's3': 'bg-amber-100 text-amber-800 border-amber-300',
            'db': 'bg-red-100 text-red-800 border-red-300',
        };
        const labels = { 'db+s3': 'DB + S3', 's3': 'S3 ONLY', 'db': 'DB ONLY' };
        return (
            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border ${styles[src] || ''}`}>
                {labels[src] || src}
            </span>
        );
    };

    return (
        <div className="space-y-4" data-testid="files-tab">
            <AwsErrorBanner error={awsError} />

            {/* Stat strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={<Database className="h-4 w-4 text-indigo-600" />} label="Matching files" value={fmtInt(total)} />
                <StatCard icon={<HardDrive className="h-4 w-4 text-blue-600" />} label="Total size" value={fmtBytes(totalBytes)} />
                <StatCard icon={<Layers className="h-4 w-4 text-violet-600" />} label="Types" value={fmtInt(facets.file_types.length)} />
                <StatCard icon={<Snowflake className="h-4 w-4 text-cyan-600" />} label="Storage classes" value={fmtInt(facets.storage_classes.length)} />
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500">Search filename / path</Label>
                            <Input value={filters.search} onChange={e => setFilter('search', e.target.value)} placeholder="e.g. Vinylmaster" data-testid="files-search-input" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500">File type</Label>
                            <select value={filters.file_type} onChange={e => setFilter('file_type', e.target.value)} className="w-full border rounded px-2 py-1.5 bg-white text-sm h-9" data-testid="files-filter-type">
                                <option value="">All</option>
                                {facets.file_types.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500">Source</Label>
                            <select value={filters.source} onChange={e => setFilter('source', e.target.value)} className="w-full border rounded px-2 py-1.5 bg-white text-sm h-9" data-testid="files-filter-source">
                                <option value="">All</option>
                                <option value="db+s3">DB + S3</option>
                                <option value="s3">S3 only</option>
                                <option value="db">DB only</option>
                                <option value="unmatched">Unmatched (no order)</option>
                            </select>
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500">Storage class</Label>
                            <select value={filters.storage_class} onChange={e => setFilter('storage_class', e.target.value)} className="w-full border rounded px-2 py-1.5 bg-white text-sm h-9">
                                <option value="">All</option>
                                {facets.storage_classes.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500">Customer ID</Label>
                            <Input value={filters.customer_id} onChange={e => setFilter('customer_id', e.target.value)} placeholder="uuid" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500">Order ID</Label>
                            <Input value={filters.order_id} onChange={e => setFilter('order_id', e.target.value)} placeholder="uuid" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500">Min size (MB)</Label>
                            <Input type="number" min="0" value={filters.min_size ? +filters.min_size / (1024 * 1024) : ''} onChange={e => setFilter('min_size', e.target.value ? Math.round(+e.target.value * 1024 * 1024) : '')} />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500">Max size (MB)</Label>
                            <Input type="number" min="0" value={filters.max_size ? +filters.max_size / (1024 * 1024) : ''} onChange={e => setFilter('max_size', e.target.value ? Math.round(+e.target.value * 1024 * 1024) : '')} />
                        </div>
                    </div>
                    <div className="mt-2 flex justify-between text-xs text-slate-500">
                        <span>{loading ? 'Loading…' : `Page ${page} / ${totalPages}`}</span>
                        <Button size="sm" variant="ghost" onClick={() => setFilters({ customer_id: '', order_id: '', file_type: '', source: '', storage_class: '', min_size: '', max_size: '', search: '' })}>
                            Clear filters
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="files-table">
                            <thead className="bg-slate-50">
                                <tr className="border-b text-left text-slate-500 uppercase">
                                    <th className="py-2 px-3">Filename</th>
                                    <th className="py-2 px-3">Customer</th>
                                    <th className="py-2 px-3">Order</th>
                                    <th className="py-2 px-3">Type</th>
                                    <th className="py-2 px-3 text-right">Size</th>
                                    <th className="py-2 px-3">Storage class</th>
                                    <th className="py-2 px-3">Modified</th>
                                    <th className="py-2 px-3">Source</th>
                                    <th className="py-2 px-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={`${r.id}-${r.s3_key}`} className="border-b hover:bg-slate-50" data-testid={`file-row-${r.id}`}>
                                        <td className="py-1.5 px-3 max-w-xs truncate" title={`${r.original_filename}\n${r.s3_path}`}>
                                            {r.original_filename}
                                        </td>
                                        <td className="py-1.5 px-3">{r.customer_name || '—'}</td>
                                        <td className="py-1.5 px-3 font-mono">{r.order_number || '—'}</td>
                                        <td className="py-1.5 px-3">{r.file_type}</td>
                                        <td className="py-1.5 px-3 text-right tabular-nums">{fmtBytes(r.size)}</td>
                                        <td className="py-1.5 px-3">{r.storage_class || '—'}</td>
                                        <td className="py-1.5 px-3 text-slate-500">{r.last_modified ? new Date(r.last_modified).toLocaleDateString('da-DK') : '—'}</td>
                                        <td className="py-1.5 px-3"><SourceBadge src={r.source} /></td>
                                        <td className="py-1.5 px-3 text-right whitespace-nowrap">
                                            <Button size="sm" variant="ghost" onClick={() => download(r)} disabled={r.source === 'db'} data-testid={`download-btn-${r.id}`}>
                                                Download
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => copyPath(r)}>Copy path</Button>
                                            {r.source === 's3' && (
                                                <Button size="sm" variant="ghost" onClick={() => repair(r)} disabled={repairing === r.s3_key || !r.order_id} title={!r.order_id ? "S3 key doesn't include an order ID — cannot auto-repair" : ''} data-testid={`repair-btn-${r.id}`}>
                                                    {repairing === r.s3_key ? 'Repairing…' : 'Repair'}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {rows.length === 0 && !loading && (
                                    <tr><td colSpan={9} className="text-center py-6 text-slate-500">No files match the current filters.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            {/* Pagination */}
            <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">{fmtInt(total)} files total</span>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} data-testid="page-prev">Previous</Button>
                    <span className="text-slate-600 self-center">Page {page} of {totalPages}</span>
                    <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="page-next">Next</Button>
                </div>
            </div>
        </div>
    );
};



// ---------- IAM policy helper --------------------------------------------
const IamPolicyHelper = () => {
    const policy = {
        Version: "2012-10-17",
        Statement: [
            {
                Sid: "OwntuneStorageDashboardRead",
                Effect: "Allow",
                Action: [
                    "s3:ListAllMyBuckets",
                    "s3:GetBucketLocation",
                    "s3:GetLifecycleConfiguration",
                    "s3:PutLifecycleConfiguration",
                    "s3:GetBucketTagging",
                    "cloudwatch:GetMetricStatistics",
                    "cloudwatch:ListMetrics",
                    "ce:GetCostAndUsage",
                    "ce:GetDimensionValues",
                    "ce:GetCostForecast",
                ],
                Resource: "*",
            },
        ],
    };
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> IAM policy JSON</CardTitle>
                <CardDescription>Attach this read-only policy to the AWS user backing this app (usually <code>vinylflow-app</code>).</CardDescription>
            </CardHeader>
            <CardContent>
                <pre className="bg-slate-900 text-slate-100 text-[11px] rounded-lg p-3 overflow-auto max-h-96">
                    <code>{JSON.stringify(policy, null, 2)}</code>
                </pre>
                <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    onClick={() => { navigator.clipboard.writeText(JSON.stringify(policy, null, 2)); toast.success('IAM policy copied to clipboard'); }}
                >
                    Copy policy JSON
                </Button>
            </CardContent>
        </Card>
    );
};

// ---------- Main page ----------------------------------------------------
export default function StorageManagementPage() {
    return (
        <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold flex items-center gap-2">
                    <HardDrive className="h-6 w-6 text-indigo-600" />
                    Storage Management
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    Live AWS S3 statistics, lifecycle policies, per-customer/order breakdowns, and cost analytics.
                </p>
            </div>
            <Tabs defaultValue="overview">
                <TabsList>
                    <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                    <TabsTrigger value="files" data-testid="tab-files">All Files</TabsTrigger>
                    <TabsTrigger value="lifecycle" data-testid="tab-lifecycle">Lifecycle</TabsTrigger>
                    <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
                    <TabsTrigger value="cost" data-testid="tab-cost">Cost</TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="mt-4"><OverviewTab /></TabsContent>
                <TabsContent value="files" className="mt-4"><FilesTab /></TabsContent>
                <TabsContent value="lifecycle" className="mt-4"><LifecycleTab /></TabsContent>
                <TabsContent value="analytics" className="mt-4"><AnalyticsTab /></TabsContent>
                <TabsContent value="cost" className="mt-4"><CostTab /></TabsContent>
            </Tabs>
        </div>
    );
}
